import { type FC, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "components/buttons/button/Button";
import { isNil, noop, once } from "lodash";
import intersection from "lodash/intersection";
import { connect, type ConnectedProps } from "react-redux";

import ConfirmLicensingStep from "../commonSteps/confirmLicensingStep/ConfirmLicensingStep";
import NotifyStep from "../commonSteps/notifyStep/NotifyStep";
import UserListStep from "../commonSteps/userAssignmentSteps/userListStep/UserListStep";
import { TemplateTypes, RolePermissions, type SortingDirection, CommunicationChannels } from "../../../enums";
import Strings from "../../../enums/strings";
import { Context } from "enums/licenseContext";
import { type AssignedUser, type NotifyStepSettings, type User, bindAction } from "../../../interfaces";
import ModalWithSteps from "../../modal/ModalWithSteps";
import { type Filters } from "../../../utils/queryUtils";
import PeopleListFirstStepButtons from "../commonSteps/peopleListFirstStepButtons/PeopleListFirstStepButtons";
import useNotifyConfig from "../../../features/SystemNotifications/hooks/useNotifyConfig";
import { initialNotifyConfigDefault } from "../../../features/SystemNotifications/config";
import { NotifyStepSwitch } from "../../notifyStep/NotifyStepSwitch";
import SearchInput from "../../searchInput/SearchInput";
import useToggle from "../../../hooks/useToggle";
import { useFeatureFlag } from "../../../hooks/useFeatureFlag";
import { FeatureFlags } from "../../../featureFlags";
import { type LicenseConfirmationFormRef } from "../../../features/Licensing/ContentAssignmentModalLicensingSteps/LicenseConfirmationModalContent/LicenseConfirmationBlockV2/LicenseConfirmationBlock";
import PaymentModal from "../../../features/Licensing/Packs/PaymentModal/PaymentModal";
import NotificationModal, { type NotificationModalState } from "../../modal/NotificationModal/NotificationModal";
import Observable from "../../../utils/Observable";
import { updateLicensePackInfo } from "../../../features/Licensing/ContentAssignmentModalLicensingSteps/state/slices/licenseConfirmationInfoSlice";
import { SwitchEntityTypes } from "../../../enums/switchEntityTypes";
import { type AppDispatch, type RootState } from "../../../features/Application/globaltypes/redux";

import "./usersAssignmentModal.scss";

export interface UsersAssignmentModalConfig {
  header?: string;
  loadPage: (
    skip: number,
    top: number,
    sortingColumnName: string,
    sortingDirection: SortingDirection,
    appliedFilter: Filters,
  ) => void;
  usersAmount: number;
  isListLoading: boolean;
  isStepLoading: boolean;
  users: User[];
  onSecondStep: (selectedIds: number[]) => Promise<boolean>;
  onConfirm: (notificationSettings?: NotifyStepSettings) => Promise<void>;
  showModal: boolean;
  onCancel: () => void;
  renderTrigger?: (closeModal: () => void) => React.ReactElement;
  alreadyAssignedUserTooltip?: string;
  assignmentEntityType?: string;
  multipleAssignment: boolean;
  focusedEntityIds: number[];
  usersAssignments: {
    items: { userId: number }[];
    comparer: (assignment: { userId: number; groupId?: number }, id: number) => boolean;
  };
  search: string;
  onSearchChanged: (value: string) => void;
  setReloadListItems: (reloadItems: (enableSorting: boolean) => void) => void;
  showRoleFilter: boolean;
  disableNotifications?: boolean;
  skipNotifyStep?: boolean;
  notifyTemplateType?: TemplateTypes;
}

const initNotifyConfig = {
  ...initialNotifyConfigDefault,
  shouldNotifyUsers: false,
};

export type UsersAssignmentModalProps = UsersAssignmentModalConfig & PropsFromRedux;

export const UsersAssignmentModal: FC<UsersAssignmentModalProps> = (props: UsersAssignmentModalProps) => {
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [appliedFilter, setFilter] = useState<Filters>({});
  const [isDataValid, setIsDataValid] = useState(true);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const onPreviousNotifyStepRef = useRef<() => Promise<void>>();
  const getNotifySettingsRef = useRef<() => Promise<NotifyStepSettings | undefined>>();
  const [notifyConfig, { setNotifyConfig, resetNotifyConfig, shouldNotify, communicationChannel }] =
    useNotifyConfig(initNotifyConfig);

  const [skipLicensingStep, setSkipLicensingStep] = useState(false);
  const [showPaymentModal, togglePaymentModal] = useToggle(false);
  const additionalPurchaseEnabled = useFeatureFlag(FeatureFlags.AdditionalPurchase);
  const licenseConfirmationFormRef = useRef<LicenseConfirmationFormRef>({ getValues: () => [] });
  const onTriggerNotificationModalObserver = useMemo(() => new Observable(), []);

  const { showModal } = props;

  useEffect(() => {
    if (!showModal) {
      setSelectedIds([]);
      setFilter({});
    }
  }, [showModal]);

  useEffect(() => {
    !showModal && resetNotifyConfig();
  }, [showModal, resetNotifyConfig]);

  const renderUserListStepActions = (nextStep: () => void) => {
    const { onSecondStep, skipNotifyStep, onConfirm } = props;
    return (closeModal: () => void) => (
      <PeopleListFirstStepButtons
        onCancel={() => {
          closeModal();
          onCancel();
        }}
        onNext={async () => {
          const shouldShowModal = await onSecondStep(selectedIds);
          setSkipLicensingStep(!shouldShowModal);

          if (!shouldShowModal && skipNotifyStep) return onConfirm();

          return nextStep();
        }}
        nextDisabled={selectedIds.length === 0 || isStepLoading}
      />
    );
  };

  const renderLicensingConfirmationStepActions = (nextStep: () => void, prevStep: () => void) => {
    const { skipNotifyStep, onConfirm, isPurchaseNeeded } = props;

    const shouldSkip = skipNotifyStep || (isPurchaseNeeded && additionalPurchaseEnabled);
    const actionHandler = shouldSkip ? () => onConfirm() : nextStep;

    let actionText = "Next";

    if (isPurchaseNeeded && additionalPurchaseEnabled) {
      actionText = "Proceed to Payment";
    } else if (skipNotifyStep) {
      actionText = "Finish";
    }

    return (closeModal: () => void) => (
      <>
        <Button primary className="previous" content="Previous" onClick={prevStep} />
        <Button
          primary
          content={actionText}
          onClick={once(async () => {
            if (additionalPurchaseEnabled && isPurchaseNeeded) {
              props.updateLicensePackInfoAction?.(licenseConfirmationFormRef.current.getValues());
              togglePaymentModal();
              closeModal();
            } else {
              actionHandler();
              if (shouldSkip) closeModal();
            }
          })}
          disabled={!isDataValid}
        />
      </>
    );
  };

  const renderNotifyStepActions = (_nextStep: () => void, prevStep: () => void) => {
    const { onConfirm } = props;
    return (closeModal: () => void) => (
      <>
        <Button
          primary
          className="previous"
          content="Previous"
          onClick={() => {
            onPreviousNotifyStepRef?.current?.();
            prevStep();
          }}
        />
        <Button
          primary
          className="confirm"
          content="Finish"
          disabled={
            shouldNotify && ((communicationChannel === CommunicationChannels.Email && !isDataLoaded) || !isDataValid)
          }
          onClick={async () => {
            const notifySettings = await getNotifySettingsRef?.current?.();
            await onConfirm(notifySettings);
            closeModal();
          }}
        />
      </>
    );
  };

  const resetUserFilter = () => setFilter({});

  const getUsersForAssign = () => {
    const {
      users,
      usersAssignments: { items, comparer },
    } = props;
    return users.map((user) => {
      const assignedCount = focusedEntityIds.filter(
        (id) => !isNil(items.find((a) => comparer(a, id) && a.userId === user.id)),
      ).length;
      const hasAssignments = !!assignedCount;
      const isAssigned = hasAssignments && assignedCount === focusedEntityIds.length;

      return {
        ...user,
        isAssigned,
        hasAssignments,
        assignedToNumberOfEntities: items.filter((x) => x.userId === user.id).length,
      } as AssignedUser;
    });
  };

  const {
    onCancel,
    renderTrigger,
    isStepLoading,
    usersAmount,
    loadPage,
    isListLoading,
    assignmentEntityType,
    focusedEntityIds,
    multipleAssignment,
    search,
    onSearchChanged,
    setReloadListItems,
    alreadyAssignedUserTooltip,
    showRoleFilter,
    disableNotifications,
    skipNotifyStep,
  } = props;

  const onPaymentSuccess = () => {
    togglePaymentModal();
    if (skipNotifyStep) {
      props.onConfirm();
    } else {
      const entityState: NotificationModalState = {
        entityType: SwitchEntityTypes.Flow,
        templateType: props.notifyTemplateType || TemplateTypes.AddedToGroup,
      };
      onTriggerNotificationModalObserver.notify(noop, entityState);
    }
  };

  const onClosePaymentModal = () => {
    togglePaymentModal();
    onCancel();
  };

  return (
    <>
      <ModalWithSteps
        className={"users-assignment-modal"}
        renderTrigger={renderTrigger}
        isLoading={isStepLoading}
        showModal={showModal}
        onCancel={onCancel}
      >
        <UserListStep
          renderSearch={() => (
            <SearchInput placeholder="Search for users..." onChange={onSearchChanged} defaultValue={search} />
          )}
          header={props.header || "Add People"}
          renderModalActions={renderUserListStepActions}
          loadPage={loadPage}
          isLoading={isListLoading}
          users={getUsersForAssign()}
          usersCount={usersAmount}
          onSelectedListItemsChanged={setSelectedIds}
          selectedIds={selectedIds}
          applyFilter={setFilter}
          filter={appliedFilter}
          resetFilter={resetUserFilter}
          alreadyAssignedUserTooltip={alreadyAssignedUserTooltip}
          assignmentEntityType={assignmentEntityType}
          selectedParentEntitiesLength={focusedEntityIds.length}
          multipleAssignment={multipleAssignment}
          showRoleFilter={showRoleFilter}
          hasPermissionPredicate={(userPermissions) =>
            intersection(userPermissions, [RolePermissions.UsersView]).length > 0
          }
          setReloadListItems={setReloadListItems}
        />
        {!skipLicensingStep && (
          <ConfirmLicensingStep
            header={Strings.modalTitles.licenseConfirmation}
            renderModalActions={renderLicensingConfirmationStepActions}
            onIsDataValidChange={setIsDataValid}
            info={{
              groupIds: focusedEntityIds,
              userIds: selectedIds,
            }}
            context={Context.AddPeopleToGroups}
            licenseConfirmationFormRef={licenseConfirmationFormRef}
          />
        )}
        {!skipNotifyStep && (
          <NotifyStep
            preRender
            header="Notify"
            renderModalActions={renderNotifyStepActions}
            onIsDataValidChange={setIsDataValid}
            onIsDataLoaded={setIsDataLoaded}
            isDataLoaded={isDataLoaded}
            onPreviousNotifyStepRef={onPreviousNotifyStepRef}
            getNotifySettingsRef={getNotifySettingsRef}
            notifyStepDisabled={disableNotifications}
            notifyTemplateType={props.notifyTemplateType || TemplateTypes.AddedToGroup}
            renderSwitch={(switchProps) => (
              <NotifyStepSwitch
                config={notifyConfig}
                onNotifyConfigChange={setNotifyConfig}
                switchProps={switchProps}
              />
            )}
            shouldNotify={shouldNotify}
            communicationChannel={communicationChannel}
          />
        )}
      </ModalWithSteps>
      {showPaymentModal && props.paymentPacks && (
        <PaymentModal
          paymentPacks={props.paymentPacks}
          onClose={onClosePaymentModal}
          onPaymentSuccess={onPaymentSuccess}
        />
      )}
      {additionalPurchaseEnabled && (
        <NotificationModal
          onTriggerPublishContentObserver={onTriggerNotificationModalObserver}
          onConfirmAction={(notificationConfirmPayload) =>
            props.onConfirm(notificationConfirmPayload.notificationSettings)
          }
          onClose={onCancel}
        />
      )}
    </>
  );
};

UsersAssignmentModal.defaultProps = {
  focusedEntityIds: [],
};

export { UsersAssignmentModal as UsersAssignmentModalUnconnected };

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => ({
  isPurchaseNeeded: state.licensing?.availableLicenses?.isPurchaseNeeded,
  paymentPacks: state.licensing.licenseConfirmationInfo.paymentPacks,
});

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => ({
  updateLicensePackInfoAction: bindAction(updateLicensePackInfo, dispatch),
});

/* istanbul ignore next */
const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(UsersAssignmentModal);
export default ConnectedComponent;
